import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { PRICING } from "@/constants/testIds";
import { Check, MessagesSquare, Flame, Sparkles, FlaskConical } from "lucide-react";
import PricingFAQ from "@/components/PricingFAQ";
import api from "@/lib/api";
import usePromoStatus from "@/lib/usePromoStatus";

export default function Pricing() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const featuresFree = t("pricingUi.features.free", { returnObjects: true });
  const featuresBasic = t("pricingUi.features.basic", { returnObjects: true });
  const featuresPremium = t("pricingUi.features.premium", { returnObjects: true });

  // Live launch-promo state — public endpoint. Determines the effective
  // Basic price + the counter banner on the Basic card.
  const { promo, lp, loyalty, referral, params: P, m } = usePromoStatus();

  // Attribution — fire a single analytics event when the visitor arrives with
  // a source flag (e.g. ?src=tour from the guided onboarding finish button).
  useEffect(() => {
    const source = searchParams.get("src");
    if (!source) return;
    api.post("/analytics/pricing-view", {
      source,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
    }).catch(() => { /* best-effort */ });
    // Only fire once per page load, keyed by the source value.
  }, []);

  const handleSubscribe = (packageId) => {
    // Visitors — send them through the Onboarding Quiz first with the tier
    // pre-selected. After signup, the Dashboard surfaces a resume-checkout
    // banner so the paid intent isn't lost during the free account creation.
    if (!user || typeof user !== "object") {
      try { localStorage.setItem("summit_intent_tier", packageId); } catch { /* ignore */ }
      // Best-effort analytics — mirrors the ?src=tour pattern used elsewhere.
      api.post("/analytics/pricing-view", {
        source: `pricing-card-start-${packageId}`,
        referrer: typeof document !== "undefined" ? document.referrer || null : null,
      }).catch(() => { /* best-effort */ });
      navigate(`/onboarding?intent=${packageId}`);
      return;
    }
    navigate(`/checkout/agreement/${packageId}`);
  };

  // Loyalty upgrade — existing Basic members can upgrade to Premium at a
  // discounted price ($750 instead of $1,250, saving $500). This is a real
  // benefit to reward paying members; the discount is also honored server-side
  // in the checkout flow (see routes_checkout.py).
  const isBasicMember = user && typeof user === "object" && user.tier === "basic";
  const isPremiumMember = user && typeof user === "object" && user.tier === "premium";

  // Live promo — determines Basic tier display price and the urgency chip.
  // We intentionally do NOT show the exact remaining count — an unchanging
  // "48 of 50" number over a slow week reads as stagnant. A pulsing
  // "selling fast" chip creates urgency without pinning us to a number.
  const promoStage = promo?.stage; // "free" | "half" | "full" | undefined
  const basicPromoDisplay = promo && promoStage === "free"
    ? { price: 0, strike: lp.basic, label: t("pricingUi.basicPromo.first30", P) }
    : promo && promoStage === "half"
    ? { price: promo.half_price, strike: lp.basic, label: t("pricingUi.basicPromo.seats31to80", P) }
    : null;

  // Premium launch promo — first 10 Premium seats are also FREE. Independent
  // counter from Basic (`promo.premium` is nested in the /promo/status response).
  const premiumPromo = promo?.premium;
  const premiumPromoDisplay = premiumPromo && premiumPromo.stage === "free"
    ? { price: 0, strike: lp.premium, label: t("pricingUi.premiumPromo.firstSeats", { count: premiumPromo.free_cap }) }
    : null;

  const Card = ({ testId, tierKey, price, originalPrice, subtitle, features, cta, ctaTestId, onClick, popular, oneTime }) => {
    const showLoyaltyBadge = isBasicMember && tierKey === "premium" && loyalty.active && loyalty.price < lp.premium;
    const isBasicPromo = tierKey === "basic" && basicPromoDisplay && !isBasicMember;
    // Premium launch promo — only show for non-premium users (once you're
    // in, you're in; no fake discount on your existing membership).
    const isPremiumPromo = tierKey === "premium" && premiumPromoDisplay && !isPremiumMember && !showLoyaltyBadge;
    // Visitors haven't signed up yet — swap the CTA copy so it reads like an
    // on-ramp ("Start with the quiz") instead of a hard paywall push. The
    // click handler already routes visitors through /onboarding.
    const isVisitor = !user || typeof user !== "object";
    // Suppress the "Buy now" CTA for the tier the user already holds so they
    // can't accidentally purchase their existing membership again. Instead
    // we show a green "Current membership" chip that links back to /dashboard.
    const isCurrentTier =
      (tierKey === "basic" && isBasicMember) ||
      (tierKey === "premium" && isPremiumMember);
    const effectivePrice = showLoyaltyBadge
      ? loyalty.price
      : isBasicPromo ? basicPromoDisplay.price
      : isPremiumPromo ? premiumPromoDisplay.price
      : price;
    const effectiveOriginal = showLoyaltyBadge
      ? lp.premium
      : isBasicPromo ? basicPromoDisplay.strike
      : isPremiumPromo ? premiumPromoDisplay.strike
      : originalPrice;
    return (
    <div data-testid={testId} className={`bg-white rounded-2xl p-8 hover:-translate-y-1 transition-transform duration-300 relative ${popular ? "tracing-beam" : "border border-slate-200"}`}>
      {popular && (
        <div className="absolute -top-3 right-6 bg-blue-700 text-white text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-semibold">
          {t("pricingUi.mostPopular")}
        </div>
      )}
      {showLoyaltyBadge && (
        <div data-testid="pricing-loyalty-badge" className="absolute -top-3 left-6 bg-amber-500 text-slate-950 text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold">
          {t("pricingUi.loyaltyBadge", P)}
        </div>
      )}
      {isBasicPromo && (
        <div data-testid="pricing-promo-badge" className="absolute -top-3 left-6 bg-red-600 text-white text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold flex items-center gap-1.5">
          <Flame size={11} /> {basicPromoDisplay.label}
        </div>
      )}
      {isPremiumPromo && (
        <div data-testid="pricing-premium-promo-badge" className="absolute -top-3 left-6 bg-red-600 text-white text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold flex items-center gap-1.5">
          <Flame size={11} /> {premiumPromoDisplay.label}
        </div>
      )}
      {originalPrice && !showLoyaltyBadge && !isBasicPromo && !isPremiumPromo && (
        <div className="absolute -top-3 left-6 bg-amber-500 text-slate-950 text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold">
          {t("pricingUi.onSale")}
        </div>
      )}
      <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">{t(`tier.${tierKey}`)}</div>
      <div className="mt-4 flex items-baseline gap-2 flex-wrap">
        {effectiveOriginal && (
          <span className="text-slate-400 text-2xl line-through font-semibold">{m(effectiveOriginal)}</span>
        )}
        <span className="font-display font-black text-5xl text-slate-900">{m(effectivePrice)}</span>
        <span className="text-slate-500 text-sm">{oneTime ? t("pricingUi.oneTime") : t("pricingUi.perMonth")}</span>
      </div>
      {showLoyaltyBadge && (
        <>
          <p className="mt-2 text-xs text-amber-700 font-semibold">
            {t("pricingUi.loyaltyUpgradeExplainer", P)}
          </p>
          {/* Referral stack — deliberately loud to promote membership growth */}
          {referral.active && referral.percent > 0 && <div className="mt-3 relative overflow-hidden rounded-lg border-2 border-amber-500 bg-gradient-to-br from-amber-50 to-white p-3">
            <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-amber-400/25 blur-2xl pointer-events-none" />
            <div className="relative flex items-start gap-2">
              <span className="inline-flex w-6 h-6 rounded-md bg-amber-500 text-slate-950 items-center justify-center shrink-0">
                <Sparkles size={12} strokeWidth={2.5} />
              </span>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest font-black text-amber-800 leading-tight">
                  {t("pricingUi.referralStack.eyebrow", P)}
                </div>
                <div className="mt-0.5 text-[13px] font-bold text-slate-900 leading-snug">
                  {t("pricingUi.referralStack.description", P)}{" "}
                  <span className="text-amber-700">{t("pricingUi.referralStack.outcome", P)}</span>
                </div>
              </div>
            </div>
          </div>}
        </>
      )}
      {(isBasicPromo || isPremiumPromo) && (
        <div data-testid="pricing-promo-counter" className="mt-3 flex items-center gap-2">
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
          </span>
          <p className="text-xs text-red-700 font-semibold">
            {t("pricingUi.promoCounter")}
          </p>
        </div>
      )}
      <p className="mt-3 text-slate-600 text-sm">{subtitle}</p>
      <ul className="mt-8 space-y-3 text-sm">
        {(Array.isArray(features) ? features : []).map((f) => {
          const isDiscord = /discord/i.test(f);
          if (isDiscord) {
            return (
              <li
                key={f}
                data-testid="pricing-feature-discord"
                className="flex items-start gap-2 text-slate-900 font-medium bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2 -mx-1"
              >
                <MessagesSquare className="text-amber-600 shrink-0 mt-0.5" size={16} /> {f}
              </li>
            );
          }
          return (
            <li key={f} className="flex items-start gap-2 text-slate-700">
              <Check className="text-blue-700 shrink-0 mt-0.5" size={16} /> {f}
            </li>
          );
        })}
      </ul>
      {tierKey === "free" ? (
        <Link to={user && typeof user === "object" ? "/dashboard" : "/register"}>
          <Button variant="outline" className="w-full mt-8 border-2 border-slate-200 hover:border-slate-900 text-slate-900 h-11 rounded-md">
            {user && typeof user === "object" ? t("pricingUi.goToDashboard") : t("pricingUi.startFree")}
          </Button>
        </Link>
      ) : (isCurrentTier ? (
        <Link to="/dashboard">
          <Button
            data-testid={ctaTestId}
            variant="outline"
            className="w-full mt-8 h-11 rounded-md border-2 border-emerald-500 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-semibold cursor-default"
          >
            <Check size={14} className="mr-2" /> {t("pricingUi.currentMembership")}
          </Button>
        </Link>
      ) : (
        <Button data-testid={ctaTestId} onClick={onClick} className={`w-full mt-8 h-11 rounded-md text-white ${popular ? "bg-blue-800 hover:bg-blue-700" : "bg-slate-900 hover:bg-slate-800"}`}>
          {showLoyaltyBadge ? t("pricingUi.upgradeFor750", P) : (isVisitor ? t("pricingUi.startWithQuiz") : cta)}
        </Button>
      ))}
      {tierKey !== "free" && !isCurrentTier && isVisitor && (
        <p className="mt-2 text-[11px] text-slate-500 text-center leading-relaxed">
          {t("pricingUi.quizExplainer")}
        </p>
      )}
      {tierKey !== "free" && !isCurrentTier && (
        <div className="mt-3 text-[11px] text-slate-500 text-center leading-relaxed">
          <span className="font-semibold text-slate-700">{t("pricingUi.payIn4.label")}</span> {t("pricingUi.payIn4.description")}
        </div>
      )}
    </div>
    );
  };

  return (
    <div data-testid={PRICING.root} className="bg-[#F8FAFC] min-h-screen">
      <section className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-28">
        {searchParams.get("highlight") === "residency" && (
          <div
            data-testid="pricing-residency-highlight"
            className="mb-10 relative overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-slate-950 to-blue-950 text-white p-6 sm:p-8"
          >
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-amber-500/20 blur-3xl" />
            <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 border border-amber-400/30 flex items-center justify-center shrink-0">
                <FlaskConical size={22} className="text-amber-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-amber-400 font-semibold">
                  {t("pricingUi.residencyHighlight.eyebrow")}
                </div>
                <h2 className="mt-1 font-display font-bold text-2xl sm:text-3xl text-white tracking-tight">
                  {t("pricingUi.residencyHighlight.title")}
                </h2>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed max-w-2xl">
                  {t("pricingUi.residencyHighlight.description")}
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-4">{t("pricingUi.eyebrow")}</div>
          <h1 className="font-display font-black text-slate-900 text-5xl sm:text-6xl leading-[0.95] tracking-tighter">
            {t("pricingUi.titleLine1")}<br /><span className="text-blue-800">{t("pricingUi.titleLine2")}</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">{t("pricingUi.subtitle")}</p>
        </div>

        {/* Climb rail — moved here from the landing hero. Anchors each tier
            to a stage on the journey so the tier choice below feels less
            like a menu and more like picking where you're joining the climb. */}
        <div
          data-testid="pricing-climb-rail"
          className="mt-14 mb-4 rounded-3xl bg-slate-950 text-white p-5 sm:p-10 relative overflow-hidden"
        >
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-amber-500/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-blue-600/25 blur-3xl pointer-events-none" />

          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.32em] text-amber-300 font-bold text-center mb-2">{t("pricingUi.climbRail.eyebrow")}</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tighter leading-tight text-center max-w-2xl mx-auto">
              {t("pricingUi.climbRail.title")}
            </h2>

            {/* Rail */}
            <div className="mt-10 relative max-w-3xl mx-auto">
              <div className="absolute left-[4%] right-[4%] top-[9px] h-[2px] bg-white/10 rounded-full" />
              <div className="absolute left-[4%] top-[9px] h-[2px] w-[92%] bg-gradient-to-r from-blue-400 via-blue-500 to-amber-400 rounded-full opacity-70" />
              <div className="relative flex justify-between">
                {[
                  { nameKey: "pricingUi.climbRail.stages.foundations.name", tierKey: "pricingUi.climbRail.stages.foundations.tier", accent: "blue"  },
                  { nameKey: "pricingUi.climbRail.stages.curriculum.name",  tierKey: "pricingUi.climbRail.stages.curriculum.tier",  accent: "blue"  },
                  { nameKey: "pricingUi.climbRail.stages.ascent.name",      tierKey: "pricingUi.climbRail.stages.ascent.tier",      accent: "blue"  },
                  { nameKey: "pricingUi.climbRail.stages.residency.name",   tierKey: "pricingUi.climbRail.stages.residency.tier",   accent: "amber" },
                  { nameKey: "pricingUi.climbRail.stages.peak.name",        tierKey: "pricingUi.climbRail.stages.peak.tier",        accent: "peak"  },
                ].map((s) => (
                  <div key={s.nameKey} className="flex flex-col items-center w-16 sm:w-24 shrink-0">
                    <div className="relative flex items-center justify-center">
                      {s.accent === "peak" && (
                        <span className="absolute w-7 h-7 rounded-full bg-amber-500/30 animate-ping" aria-hidden />
                      )}
                      <div className={`relative rounded-full border-2 border-white ${
                        s.accent === "peak"  ? "w-5 h-5 bg-amber-500 shadow-[0_4px_14px_rgba(245,158,11,0.55)]" :
                        s.accent === "amber" ? "w-5 h-5 bg-amber-400" :
                                                "w-4 h-4 bg-blue-400"
                      }`} />
                    </div>
                    <div className={`mt-3 text-[11px] font-display font-bold tracking-tight text-center ${
                      s.accent === "peak" ? "text-amber-300" : s.accent === "amber" ? "text-amber-200" : "text-blue-100"
                    }`}>
                      {t(s.nameKey)}
                    </div>
                    <div className="mt-0.5 text-[9px] uppercase tracking-widest text-slate-400 font-semibold text-center leading-tight">
                      {t(s.tierKey)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p className="mt-10 text-center text-sm text-slate-400 leading-relaxed max-w-2xl mx-auto">
              {t("pricingUi.climbRail.description")}
            </p>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <Card testId={PRICING.cardFree} tierKey="free" price={0} subtitle={t("pricingUi.freeSubtitle")} features={featuresFree} />
          <Card testId={PRICING.cardBasic} tierKey="basic" price={lp.basic} oneTime subtitle={t("pricingUi.basicSubtitle")} features={featuresBasic} cta={t("pricingUi.subscribeBasic")} ctaTestId={PRICING.subscribeBasic} onClick={() => handleSubscribe("basic")} />
          <Card testId={PRICING.cardPremium} tierKey="premium" price={lp.premium} originalPrice={promo?.premium_anchor?.active ? promo.premium_anchor.was_price : null} oneTime subtitle={t("pricingUi.premiumSubtitle")} features={featuresPremium} cta={t("pricingUi.subscribePremium")} ctaTestId={PRICING.subscribePremium} onClick={() => handleSubscribe("premium")} popular />
        </div>

        {/* Payment methods trust bar */}
        <div
          data-testid="pricing-payment-methods"
          className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs text-slate-600 max-w-3xl mx-auto"
        >
          <span className="inline-flex items-center gap-2">
            <span className="inline-block w-8 h-5 rounded bg-slate-900 text-white text-[9px] font-bold uppercase tracking-widest flex items-center justify-center">
              <span className="mx-auto">card</span>
            </span>
            {t("pricingUi.paymentMethods.cards")}
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-5 rounded bg-black">
              <svg viewBox="0 0 24 24" width="12" height="12" className="fill-white" aria-hidden="true">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
            </span>
            {t("pricingUi.paymentMethods.applePay")}
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="inline-block px-1.5 h-5 rounded bg-[#FFA8CD] text-black text-[8px] font-black uppercase tracking-widest flex items-center justify-center">
              klarna
            </span>
            <span>
              <span className="font-semibold text-slate-800">Klarna</span>
              <span className="text-slate-400"> · {t("pricingUi.paymentMethods.klarnaTerms")}</span>
            </span>
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="inline-block px-1.5 h-5 rounded bg-[#B2FCE4] text-[#012A2A] text-[8px] font-black uppercase tracking-widest flex items-center justify-center">
              afterpay
            </span>
            <span>
              <span className="font-semibold text-slate-800">Afterpay</span>
              <span className="text-slate-400"> · {t("pricingUi.paymentMethods.afterpayTerms")}</span>
            </span>
          </span>
        </div>
        <p className="mt-2 text-[10px] text-slate-400 text-center max-w-xl mx-auto leading-relaxed">
          {t("pricingUi.bnplDisclaimer")}
        </p>
      </section>

      <PricingFAQ />
    </div>
  );
}